/**
 * Routines item parsing + display derivation (SYN-1506, spec §7.3/§7.4).
 *
 * Command's `GET /api/routines` returns listings + ORG readiness merged into
 * items, each carrying a BFF-computed `tier` (spec §9). THE TIER IS CONSUMED,
 * NEVER RE-DERIVED HERE — this module only parses the payload fail-closed and
 * derives presentation (names, setup status, grouping, facets) from it.
 *
 * Buyer language only: every label that reaches primary copy routes through
 * the SYN-1486 dictionary helpers; raw ids stay in Technical details.
 */

import {
  normalizeListing,
  listingCategory,
  listingDescription,
  listingTitle,
  type ContractListing,
} from "./listings";
import {
  cardCategory,
  cardDescription,
  cardTitle,
  effectiveState,
  normalizeSnapshot,
  type ReadinessSnapshot,
} from "./readiness";
import { setupStatusLabel, type SetupStatus } from "./buyer-language";
import {
  ARTIFACT_ACCESS_SHAPE_VERSION,
  parseArtifactAccess,
  type ArtifactAccess,
  type ArtifactAccessAction,
  type ArtifactSetupState,
} from "./artifact-access";
import { parseArtifactActivitySummary, type ArtifactActivitySummary } from "./activity";

/** BFF-computed presentation tier (spec §9). */
export type RoutineTier = "routine" | "contract";

/** Caller's latest access request for an item — ATLAS state (SYN-1519). */
export interface RoutineAccessRequest {
  id: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
}

/**
 * The org's tier pin on an item (SYN-1524) — Command-local presentation
 * state. When set, the BFF-computed `tier` is "contract" via the spec §9
 * first clause; removing the pin reverts to the derived tier. Ceremony
 * only — a pin grants nothing.
 */
export interface RoutinePin {
  createdBy: string;
  reason: string;
  createdAt: string;
}

export interface RoutineItem {
  /** Contract id — the stable cross-source identity and the detail-route param. */
  id: string;
  listingId?: string;
  /**
   * Computed in the BFF at the listings/readiness merge. Parsed strictly:
   * anything that isn't "contract" reads as "routine" — the same default the
   * derivation rule itself ends on — so a malformed payload can't invent
   * ceremony the BFF didn't compute.
   */
  tier: RoutineTier;
  listing: ContractListing | null;
  readiness: ReadinessSnapshot | null;
  /**
   * The caller's own latest request for this item, passed through from Atlas
   * by the BFF (SYN-1519). Parsed fail-closed: unknown statuses read as no
   * request, so a malformed payload can't paint a Requested/Approved chip.
   */
  accessRequest: RoutineAccessRequest | null;
  /**
   * The org's tier pin on this item (SYN-1524), if any. Parsed fail-closed:
   * a malformed pin reads as none. Distinguishes PINNED contract-grade
   * (admin can remove, reverting to derived) from DERIVED contract-grade
   * (governance facts — no control to lower, ever).
   */
  pin: RoutinePin | null;
  artifactAccess: ArtifactAccess;
  activity?: ArtifactActivitySummary;
}

export interface RoutinesPayload {
  items: RoutineItem[];
  errors: { listings: string | null; readiness: string | null };
}

const CONTRACT_AUTHOR_CAPABILITY_ID = "cap.contract.author";

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function parseTier(value: unknown): RoutineTier {
  return value === "contract" ? "contract" : "routine";
}

const ACCESS_REQUEST_STATUSES = new Set(["pending", "approved", "rejected"]);

function parseAccessRequest(raw: unknown): RoutineAccessRequest | null {
  const r = asRecord(raw);
  const id = typeof r.id === "string" && r.id.length > 0 ? r.id : undefined;
  const status =
    typeof r.status === "string" && ACCESS_REQUEST_STATUSES.has(r.status) ? r.status : undefined;
  if (!id || !status) {
    return null;
  }
  return {
    id,
    status: status as RoutineAccessRequest["status"],
    createdAt: typeof r.createdAt === "string" ? r.createdAt : "",
  };
}

/** Parse the org tier pin (SYN-1524) fail-closed: malformed reads as none. */
function parsePin(raw: unknown): RoutinePin | null {
  const r = asRecord(raw);
  if (typeof r.reason !== "string" || r.reason.length === 0) {
    return null;
  }
  return {
    createdBy: typeof r.createdBy === "string" ? r.createdBy : "",
    reason: r.reason,
    createdAt: typeof r.createdAt === "string" ? r.createdAt : "",
  };
}

/** Parse one merged item; items without a usable id are dropped. */
export function normalizeRoutineItem(raw: unknown): RoutineItem | undefined {
  const r = asRecord(raw);
  const id = typeof r.id === "string" && r.id.length > 0 ? r.id : undefined;
  if (!id) {
    return undefined;
  }
  const listing = r.listing ? (normalizeListing(asRecord(r.listing)) ?? null) : null;
  const readiness = r.readiness ? normalizeSnapshot(asRecord(r.readiness)) : null;
  const base = {
    id,
    listingId: typeof r.listingId === "string" && r.listingId.length > 0 ? r.listingId : undefined,
    tier: parseTier(r.tier),
    listing,
    readiness,
    accessRequest: parseAccessRequest(r.accessRequest),
    pin: parsePin(r.pin),
  };
  return {
    ...base,
    artifactAccess: parseArtifactAccess(r.artifactAccess) ?? deriveRoutineArtifactAccess(base),
    activity: parseArtifactActivitySummary(r.activity) ?? undefined,
  };
}

function parseSectionError(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function parseRoutinesPayload(raw: unknown): RoutinesPayload {
  const r = asRecord(raw);
  const items = Array.isArray(r.items)
    ? r.items.map(normalizeRoutineItem).filter((item): item is RoutineItem => item !== undefined)
    : [];
  const errors = asRecord(r.errors);
  return {
    items,
    errors: {
      listings: parseSectionError(errors.listings),
      readiness: parseSectionError(errors.readiness),
    },
  };
}

// ---------------------------------------------------------------------------
// Display derivation (presentation only — verdicts/tier pass through)
// ---------------------------------------------------------------------------

/** Display name — listing first, then snapshot metadata, never a UUID. */
export function routineName(item: RoutineItem): string {
  if (item.listing) {
    return listingTitle(item.listing);
  }
  if (item.readiness) {
    return cardTitle(item.readiness);
  }
  return "This routine";
}

export function routineDescription(item: RoutineItem): string | undefined {
  return (
    (item.listing ? listingDescription(item.listing) : undefined) ??
    (item.readiness ? cardDescription(item.readiness) : undefined)
  );
}

/** Sector/category line for the icon + chip — raw value (translate to render). */
export function routineCategory(item: RoutineItem): string | undefined {
  return (
    item.listing?.contract.sector ??
    (item.listing ? listingCategory(item.listing) : undefined) ??
    (item.readiness ? cardCategory(item.readiness) : undefined)
  );
}

/** Raw function facet value (translate to render). */
export function routineFunction(item: RoutineItem): string | undefined {
  return item.listing?.contract.function;
}

/**
 * Buyer setup status for the item. No readiness snapshot = no verdict: reads
 * as "Waiting" (fail-closed — discovery is not readiness), never "Ready".
 */
export function routineSetupStatus(item: RoutineItem): SetupStatus {
  if (!item.readiness) {
    return "Waiting";
  }
  return setupStatusLabel(effectiveState(item.readiness));
}

function routineArtifactSetupState(item: Pick<RoutineItem, "readiness">): ArtifactSetupState {
  switch (item.readiness?.readinessState) {
    case "ready":
      return "ready";
    case "needs_setup":
      return "needs_setup";
    case "blocked":
      return "problem";
    default:
      return "waiting";
  }
}

function hasSatisfiedAuthorityEvidence(readiness: ReadinessSnapshot | null): boolean {
  if (!readiness) {
    return false;
  }
  const isAuthorityKind = (kind: string) => kind === "grant" || kind === "role";
  if (readiness.satisfiedEvidenceKinds.some(isAuthorityKind)) {
    return true;
  }
  return readiness.evidence.some(
    (evidence) => isAuthorityKind(evidence.kind) && evidence.status === "satisfied",
  );
}

function hasSatisfiedAuthorityCapability(
  readiness: ReadinessSnapshot | null,
  capabilityId: string,
): boolean {
  if (!readiness) {
    return false;
  }
  return readiness.evidence.some(
    (evidence) =>
      (evidence.kind === "grant" || evidence.kind === "role") &&
      evidence.status === "satisfied" &&
      evidence.capabilityId === capabilityId,
  );
}

function deriveRoutineArtifactAccess(item: Omit<RoutineItem, "artifactAccess">): ArtifactAccess {
  const setupState = routineArtifactSetupState(item);
  const hasAuthority = hasSatisfiedAuthorityEvidence(item.readiness);
  const hasAuthoringAuthority = hasSatisfiedAuthorityCapability(
    item.readiness,
    CONTRACT_AUTHOR_CAPABILITY_ID,
  );
  const accessState = hasAuthority
    ? "yours"
    : item.accessRequest?.status === "pending"
      ? "requested"
      : item.accessRequest?.status === "approved"
        ? "assigned"
        : "requestable";
  const catalogState =
    item.readiness !== null || item.listing?.listingState === "visible"
      ? "listed"
      : item.listing
        ? "discoverable"
        : "hidden";
  const actions: ArtifactAccessAction[] = [];
  if (setupState === "ready" && hasAuthority) {
    actions.push(item.tier === "contract" ? "review_and_run" : "run");
  } else if (setupState === "needs_setup") {
    actions.push("finish_setup");
  }
  // Only a genuinely requestable item offers request_access. Keying off the
  // derived accessState (not a re-derived field condition) keeps an already
  // "assigned" item — an approved request awaiting readiness — from advertising
  // a request CTA the user has already satisfied.
  if (accessState === "requestable") {
    actions.push("request_access");
  }
  if (hasAuthoringAuthority) {
    actions.push("edit");
  }
  return {
    shapeVersion: ARTIFACT_ACCESS_SHAPE_VERSION,
    kind: "routine",
    id: item.id,
    title: routineName(item as RoutineItem),
    ...(routineDescription(item as RoutineItem)
      ? { description: routineDescription(item as RoutineItem) }
      : {}),
    catalogState,
    accessState,
    setupState,
    authoritySource: "atlas",
    roles: [],
    actions,
  };
}

export function routineArtifactAccess(item: RoutineItem): ArtifactAccess {
  if (item.artifactAccess) {
    return item.artifactAccess;
  }
  const { artifactAccess: _artifactAccess, ...base } = item;
  return deriveRoutineArtifactAccess(base);
}

/**
 * Available to the current user: the org can see/run it (visible listing) or
 * it has a readiness footprint (assigned through roles). Unavailable items
 * stay rendered with a non-dead-end status (spec §7.3) — never hidden, never
 * a disabled wall.
 */
export function routineAvailable(item: RoutineItem): boolean {
  if (item.readiness) {
    return true;
  }
  return item.listing?.listingState === "visible" || item.listing?.listingState === "blocked";
}

/** "Yours" tab membership: assigned to the user's org via roles ⇒ has readiness. */
export function isYours(item: RoutineItem): boolean {
  return item.readiness !== null;
}

/** Setup-status shelf order for the "Yours" tab (spec §7.3). */
export const YOURS_GROUP_ORDER: readonly SetupStatus[] = [
  "Ready",
  "Needs setup",
  "Waiting",
  "Problem",
] as const;

/** Group "Yours" items by setup status in shelf order; empty groups dropped. */
export function groupYours(
  items: RoutineItem[],
): Array<{ status: SetupStatus; items: RoutineItem[] }> {
  const byStatus = new Map<SetupStatus, RoutineItem[]>();
  for (const item of items) {
    if (!isYours(item)) {
      continue;
    }
    const status = routineSetupStatus(item);
    const list = byStatus.get(status) ?? [];
    list.push(item);
    byStatus.set(status, list);
  }
  return YOURS_GROUP_ORDER.filter((status) => (byStatus.get(status)?.length ?? 0) > 0).map(
    (status) => ({ status, items: byStatus.get(status) as RoutineItem[] }),
  );
}

export interface RoutineFilters {
  search?: string;
  /** Raw facet values — labels translate at render time (SYN-1502). */
  sector?: string;
  function?: string;
  tier?: RoutineTier;
  status?: SetupStatus;
}

export interface RoutineFacets {
  sectors: string[];
  functions: string[];
  tiers: RoutineTier[];
  statuses: SetupStatus[];
}

export function deriveRoutineFacets(items: RoutineItem[]): RoutineFacets {
  const sectors = new Set<string>();
  const functions = new Set<string>();
  const tiers = new Set<RoutineTier>();
  const statuses = new Set<SetupStatus>();
  for (const item of items) {
    const sector = routineCategory(item);
    if (sector) {
      sectors.add(sector);
    }
    const fn = routineFunction(item);
    if (fn) {
      functions.add(fn);
    }
    tiers.add(item.tier);
    statuses.add(routineSetupStatus(item));
  }
  const sort = (set: Set<string>) => [...set].sort((a, b) => a.localeCompare(b));
  return {
    sectors: sort(sectors),
    functions: sort(functions),
    tiers: [...tiers],
    statuses: YOURS_GROUP_ORDER.filter((status) => statuses.has(status)),
  };
}

function searchText(item: RoutineItem): string {
  return [
    routineName(item),
    routineDescription(item) ?? "",
    routineCategory(item) ?? "",
    routineFunction(item) ?? "",
    item.listing?.listingKey ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

export function matchesRoutineFilters(item: RoutineItem, filters: RoutineFilters): boolean {
  if (filters.tier && item.tier !== filters.tier) {
    return false;
  }
  if (filters.sector && routineCategory(item) !== filters.sector) {
    return false;
  }
  if (filters.function && routineFunction(item) !== filters.function) {
    return false;
  }
  if (filters.status && routineSetupStatus(item) !== filters.status) {
    return false;
  }
  const search = filters.search?.trim().toLowerCase();
  if (search && !searchText(item).includes(search)) {
    return false;
  }
  return true;
}

/**
 * Card/detail primary action label (spec §7.3): the verdict comes from
 * readiness, the ceremony from tier. Unavailable items get a NON-dead-end
 * status action (request-access wiring is W1.5 — not built).
 */
export type RoutinePrimaryActionKind = "run" | "review_run" | "finish_setup" | "view_status";

export interface RoutinePrimaryAction {
  kind: RoutinePrimaryActionKind;
  label: string;
}

export function routinePrimaryAction(item: RoutineItem): RoutinePrimaryAction {
  const actions = new Set(routineArtifactAccess(item).actions);
  if (actions.has("run")) {
    return { kind: "run", label: "Run" };
  }
  if (actions.has("review_and_run")) {
    return { kind: "review_run", label: "Review and run" };
  }
  if (actions.has("finish_setup")) {
    return { kind: "finish_setup", label: "Finish setup" };
  }
  return { kind: "view_status", label: "View status" };
}

/**
 * Access-request chip for an item (SYN-1519, spec §7.3/§10.3): pending reads
 * "Requested", approved reads "Approved" until readiness makes the item
 * available (the chip is then redundant — readiness is the verdict). A
 * declined request shows no chip so the Request-access button returns.
 */
export function routineAccessChip(item: RoutineItem): "Requested" | "Approved" | null {
  if (item.accessRequest?.status === "pending") {
    return "Requested";
  }
  if (item.accessRequest?.status === "approved" && !routineAvailable(item)) {
    return "Approved";
  }
  return null;
}

/**
 * Whether the card/detail should offer the working Request-access button
 * (spec §7.3 — unavailable items get Request access instead of a disabled
 * wall): not available to the caller and no chip showing — a pending or
 * freshly-approved request IS the state, a declined one frees the button.
 */
export function routineCanRequestAccess(item: RoutineItem): boolean {
  return !routineAvailable(item) && routineAccessChip(item) === null;
}

// ---------------------------------------------------------------------------
// Connection requirements (SYN-1559)
// ---------------------------------------------------------------------------

export interface ConnectionRequirement {
  key: string;
  label: string;
  sourceRef?: string;
  connectionId?: string;
  resolution: "resolved" | "unresolved" | "ambiguous";
}

/**
 * Human-readable connection requirements from listing external_dependency
 * requirements. De-duped by key; empty when the listing has none.
 */
export function connectionRequirements(
  listing: import("./listings").ContractListing | null | undefined,
): ConnectionRequirement[] {
  if (!listing) {
    return [];
  }
  const seen = new Set<string>();
  const result: ConnectionRequirement[] = [];
  for (const req of listing.requirements) {
    if (req.type !== "external_dependency") {
      continue;
    }
    if (seen.has(req.key)) {
      continue;
    }
    seen.add(req.key);
    result.push({
      key: req.key,
      label: req.label,
      sourceRef: req.sourceRef,
      connectionId: req.connectionId,
      resolution: req.connectionResolution ?? "unresolved",
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Category grouping (SYN-1558, catalog tab)
// ---------------------------------------------------------------------------

/**
 * Group catalog items by their routineCategory(). Items with no category fall
 * under "Other". Groups are sorted alphabetically with "Other" last.
 */
export function groupByCategory(
  items: RoutineItem[],
): Array<{ category: string; items: RoutineItem[] }> {
  const OTHER = "Other";
  const byCategory = new Map<string, RoutineItem[]>();
  for (const item of items) {
    const cat = routineCategory(item) ?? OTHER;
    const list = byCategory.get(cat) ?? [];
    list.push(item);
    byCategory.set(cat, list);
  }
  const sorted = [...byCategory.keys()]
    .filter((k) => k !== OTHER)
    .sort((a, b) => a.localeCompare(b));
  if (byCategory.has(OTHER)) {
    sorted.push(OTHER);
  }
  return sorted.map((category) => ({
    category,
    items: byCategory.get(category) as RoutineItem[],
  }));
}
